import React, { useEffect, useMemo, useState } from "react";
import config from "../config/config";

const defaultSettings = {
  enabled: true,
  position: "fixed",
  placement: "top",
  width: "100%",
  background_color: "#f8fbff",
  text_color: "#0e1726",
  border: "",
  padding: "0px",
  margin: "0",
  animation_type: "marquee",
  animation_speed: 44,
  pause_on_hover: true,
  autoplay: true,
  show_close_button: true,
  height_px: 58,
};

function normalizeMessages(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((entry, index) => {
      if (typeof entry === "string") {
        return { id: `msg-${index}`, title: "", text: entry };
      }
      return {
        id: entry?.id ?? `msg-${index}`,
        title: entry?.title || "",
        text: entry?.text || entry?.message || "",
      };
    })
    .filter((entry) => entry.text);
}

function buildTickerTrack(messages) {
  if (!messages.length) return [];
  return [...messages, ...messages];
}

export default function NewsTicker({
  messages: incomingMessages = [],
  settings: incomingSettings = {},
  perMessageSpeed = [],
}) {
  const [settings, setSettings] = useState({ ...defaultSettings, ...incomingSettings });
  const [messages, setMessages] = useState(normalizeMessages(incomingMessages));
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setSettings((current) => ({ ...current, ...defaultSettings, ...incomingSettings }));
  }, [incomingSettings]);

  useEffect(() => {
    setMessages(normalizeMessages(incomingMessages));
  }, [incomingMessages]);

  useEffect(() => {
    if (incomingMessages?.length) return;
    let cancelled = false;

    async function loadTicker() {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/newsticker/public`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setSettings((current) => ({ ...current, ...defaultSettings, ...(data?.settings || {}) }));
        setMessages(normalizeMessages(data?.messages || []));
      } catch (_error) {
        // Silent: the dashboards should remain usable without the ticker.
      }
    }

    loadTicker();
    return () => {
      cancelled = true;
    };
  }, [incomingMessages]);

  const normalizedMessages = useMemo(() => normalizeMessages(messages), [messages]);
  const trackMessages = useMemo(() => buildTickerTrack(normalizedMessages), [normalizedMessages]);

  if (!settings.enabled || dismissed || !normalizedMessages.length) {
    return null;
  }

  const height = Math.max(Number(settings.height_px) || 58, 52);
  const speedFactor = Math.max(Number(settings.animation_speed) || 44, 24);
  const marqueeDuration = Math.max(trackMessages.length * speedFactor * 0.85, 42);

  return (
    <>
      <style>{`
        @keyframes e4eSignalMarquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>
      <div
        style={{
          position: "fixed",
          top: 8,
          left: 0,
          right: 0,
          zIndex: 120,
          pointerEvents: "none",
          paddingInline: "12px",
        }}
      >
        <div
          style={{
            width: "min(2440px, 100%)",
            margin: "0 auto",
            pointerEvents: "auto",
            borderRadius: "20px",
            border: "1px solid rgba(198, 212, 234, 0.9)",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,251,255,0.98) 52%, rgba(241,245,251,0.98))",
            boxShadow:
              "0 18px 48px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.7)",
            backdropFilter: "blur(14px)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "-80% auto auto -6%",
              width: 320,
              height: 320,
              borderRadius: "999px",
              background: "radial-gradient(circle, rgba(37,99,255,0.12) 0%, rgba(37,99,255,0.04) 34%, transparent 72%)",
              filter: "blur(32px)",
              opacity: 0.85,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: "auto -8% -120% auto",
              width: 420,
              height: 420,
              borderRadius: "999px",
              background: "radial-gradient(circle, rgba(25,184,201,0.12) 0%, rgba(25,184,201,0.04) 38%, transparent 72%)",
              filter: "blur(38px)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              minHeight: height,
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              alignItems: "center",
              gap: 16,
              padding: "12px 16px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
                padding: "10px 12px",
                borderRadius: 16,
                border: "1px solid rgba(198, 212, 234, 0.9)",
                background: "rgba(255,255,255,0.7)",
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "999px",
                  background: "#2563ff",
                  boxShadow: "0 0 0 4px rgba(37,99,255,0.12)",
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "Sora, sans-serif",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(70, 85, 111, 0.82)",
                  }}
                >
                  Signalband
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(14, 23, 38, 0.9)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Experts4Europe live
                </div>
              </div>
            </div>

            <div
              style={{
                position: "relative",
                overflow: "hidden",
                minWidth: 0,
                borderRadius: 16,
                border: "1px solid rgba(220, 229, 242, 0.95)",
                background: "rgba(255,255,255,0.62)",
                padding: "10px 0",
              }}
            >
              {settings.animation_type === "none" ? (
                <div style={{ paddingInline: 18, color: settings.text_color, fontSize: 14, lineHeight: 1.4 }}>
                  {normalizedMessages[0]?.title ? `${normalizedMessages[0].title} · ` : ""}
                  {normalizedMessages[0]?.text}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    width: "max-content",
                    animation: settings.autoplay ? `e4eSignalMarquee ${marqueeDuration}s linear infinite` : "none",
                    animationPlayState: settings.pause_on_hover ? "running" : "running",
                  }}
                  onMouseEnter={(event) => {
                    if (settings.pause_on_hover) {
                      event.currentTarget.style.animationPlayState = "paused";
                    }
                  }}
                  onMouseLeave={(event) => {
                    if (settings.pause_on_hover) {
                      event.currentTarget.style.animationPlayState = "running";
                    }
                  }}
                >
                  {trackMessages.map((message, index) => {
                    const messageSpeed = perMessageSpeed[index % Math.max(perMessageSpeed.length || 1, 1)] || 1;
                    return (
                      <div
                        key={`${message.id}-${index}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 12,
                          paddingInline: 20,
                          color: settings.text_color,
                          opacity: 0.96,
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          transform: `scale(${Math.min(Math.max(messageSpeed, 0.85), 1.1)})`,
                          transformOrigin: "left center",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: "rgba(37,99,255,0.08)",
                            border: "1px solid rgba(37,99,255,0.16)",
                            color: "rgba(37,99,255,0.86)",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.14em",
                          }}
                        >
                          {message.title || "Signal"}
                        </span>
                        <span style={{ color: "rgba(14, 23, 38, 0.9)" }}>{message.text}</span>
                        <span style={{ color: "rgba(37,99,255,0.4)", fontSize: 12 }}>•</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {settings.show_close_button ? (
              <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label="Signalband schließen"
                style={{
                  height: 40,
                  minWidth: 40,
                  paddingInline: 12,
                  borderRadius: 14,
                  border: "1px solid rgba(220, 229, 242, 0.95)",
                  background: "rgba(255,255,255,0.7)",
                  color: "rgba(14, 23, 38, 0.92)",
                  cursor: "pointer",
                  transition: "background 160ms ease, transform 160ms ease, border-color 160ms ease",
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = "rgba(37,99,255,0.08)";
                  event.currentTarget.style.borderColor = "rgba(37,99,255,0.24)";
                  event.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "rgba(255,255,255,0.7)";
                  event.currentTarget.style.borderColor = "rgba(220, 229, 242, 0.95)";
                  event.currentTarget.style.transform = "translateY(0)";
                }}
              >
                ×
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
